import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import api from '../api';

/**
 * Auto-heal leftover state from the LEGACY impersonation flow (pre-Aug 2026).
 * The old flow overwrote localStorage.clanster_access_token with a
 * test-user JWT and stashed the admin's JWT in localStorage.admin_backup_token.
 * After redeploying to the new tab-based Sandbox Launcher, users landed here
 * still authenticated as `test_creator` — every /admin/* call then 403's
 * with "Admin access required". This runs once on mount and restores the
 * admin JWT into place, silently.
 */
function _healLegacyImpersonation() {
  try {
    const TOKEN_KEY = 'clanster_access_token';
    const backup = localStorage.getItem('admin_backup_token');
    const impersonating = localStorage.getItem('impersonating_handle') || localStorage.getItem('impersonating_user_id');
    if (backup && impersonating) {
      localStorage.setItem(TOKEN_KEY, backup);
      localStorage.removeItem('admin_backup_token');
      localStorage.removeItem('impersonating_user_id');
      localStorage.removeItem('impersonating_handle');
      // eslint-disable-next-line no-console
      console.info('[admin-seed] Restored admin session from legacy impersonation state.');
      return true;
    }
  } catch { /* localStorage blocked — nothing to do */ }
  return false;
}

/**
 * Admin > Seed Network
 *
 * Manages the founder-controlled bootstrap accounts. Every account here is
 * flagged `is_seed=true` permanently in the DB. From this page you can:
 *   - Bulk-create the initial batch (40 users + 12 creators by default)
 *   - Fund an account from the seed pool (tracked separately from real deposits)
 *   - Impersonate any seed account to upload a DP, edit bio, post, or trade
 *   - Start/stop the auto-trading simulator with a single button
 */
export default function AdminSeed() {
  const [users, setUsers] = useState([]);
  const [totalFunded, setTotalFunded] = useState(0);
  const [sim, setSim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [testAccounts, setTestAccounts] = useState({});
  const [testBusy, setTestBusy] = useState(false);
  const [inSandboxTab, setInSandboxTab] = useState(false);

  // Detect if we're currently INSIDE a sandbox tab (opened via the Launcher).
  // If so, sessionStorage overrides localStorage per the api.js interceptor,
  // and every /admin/* call would 403 because Bearer resolves to a fan/creator.
  // We surface a "Return to admin" banner so the user can recover without
  // hunting for the right tab.
  useEffect(() => {
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('sandbox_handle')) {
      setInSandboxTab(true);
    }
  }, []);

  const returnToAdminSession = () => {
    try {
      sessionStorage.removeItem('clanster_access_token');
      sessionStorage.removeItem('sandbox_handle');
    } catch { /* storage blocked — nothing to do */ }
    window.location.reload();
  };

  // Silent self-heal for legacy impersonation. Runs before the first render's
  // API calls so /admin/seed/* requests use the restored admin JWT.
  useEffect(() => {
    const didHeal = _healLegacyImpersonation();
    if (didHeal) {
      setMsg({ kind: 'success', text: 'Restored admin session from a previous impersonation. Sandbox tools ready.' });
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [u, s, t] = await Promise.all([
        api.get('/admin/seed/users'),
        api.get('/admin/seed/simulator/status'),
        api.get('/admin/seed/test-accounts'),
      ]);
      setUsers(u.data.users || []);
      setTotalFunded(u.data.total_funded_pts || 0);
      setSim(s.data);
      setTestAccounts(t.data || {});
    } catch (e) {
      setMsg({ kind: 'error', text: 'Failed to load seed network — ' + (e.response?.data?.detail || e.message) });
    } finally {
      setLoading(false);
    }
  }, []);

  const resetTestAccounts = async () => {
    if (inSandboxTab) {
      returnToAdminSession();
      return;
    }
    if (!window.confirm('Reset all 11 sandbox accounts? This deletes and re-creates test_creator + test_fan_1..10 with fresh 10,000 pt balances. Any real user data is untouched.')) return;
    setTestBusy(true);
    try {
      const { data } = await api.post('/admin/seed/test-accounts/reset');
      const count = Object.keys(data.accounts || {}).length;
      setMsg({ kind: 'success', text: `Reset complete — ${count} sandbox accounts ready.` });
      await refresh();
    } catch (e) {
      setMsg({ kind: 'error', text: 'Reset failed — ' + (e.response?.data?.detail || e.message) });
    } finally {
      setTestBusy(false);
    }
  };

  // Sandbox Launcher — mint an 8h impersonation JWT and open in a NEW TAB.
  // The /impersonate landing page stores the JWT in sessionStorage (per-tab)
  // so the admin's session in this tab stays intact and each sandbox tab
  // holds its own identity simultaneously.
  const openSandboxTab = async (acct) => {
    if (!acct) return;
    // If we're INSIDE a sandbox tab (sessionStorage has the sandbox JWT),
    // launching another sandbox from here would 403. Auto-recover by
    // clearing the sandbox session and reloading — the admin's JWT still
    // lives in localStorage and will take over.
    if (inSandboxTab) {
      returnToAdminSession();
      return;
    }
    try {
      const { data } = await api.post(
        `/admin/seed/users/${acct.id}/impersonate`,
        // no_cookie:true is CRITICAL — cookies are shared across all same-origin
        // tabs and would otherwise hijack the admin's own session in this tab.
        // The new tab will authenticate exclusively via sessionStorage JWT.
        { ttl_minutes: 480, no_cookie: true },
      );
      const url = `/impersonate?token=${encodeURIComponent(data.access_token)}&handle=${encodeURIComponent(acct.handle || '')}`;
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      alert('Sandbox launch failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  useEffect(() => { refresh(); }, [refresh]);

  // Auto-refresh simulator status every 5s while it's running
  useEffect(() => {
    if (!sim?.enabled) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get('/admin/seed/simulator/status');
        setSim(data);
      } catch { /* silent */ }
    }, 5000);
    return () => clearInterval(t);
  }, [sim?.enabled]);

  const resetSeedNetwork = async () => {
    if (!window.confirm(`⚠️ NUKE the entire seed network?\n\nThis deletes ALL ${users.length} seed accounts + their clans, holdings, transactions, posts, chats and points. Real user data is untouched.\n\nAfter this you can click "Create initial batch" to spin up a fresh 18.`)) return;
    setBusy(true);
    setMsg(null);
    try {
      const { data } = await api.post('/admin/seed/users/reset');
      setMsg({ kind: 'success', text: `Wiped ${data.deleted_users} seed users + ${data.deleted_clans} clans. Ready for a fresh batch.` });
      await refresh();
    } catch (e) {
      setMsg({ kind: 'error', text: 'Reset failed — ' + (e.response?.data?.detail || e.message) });
    } finally {
      setBusy(false);
    }
  };

  const createBatch = async () => {
    if (!window.confirm('Create 12 users + 6 creators? Each will be funded with 500 pts. This runs once — don\'t double-tap.')) return;
    setBusy(true);
    setMsg(null);
    try {
      const { data } = await api.post('/admin/seed/users/batch', {
        users_count: 12, creators_count: 6, initial_fund_pts: 500,
      });
      setMsg({ kind: 'success', text: `Created ${data.created} accounts (${data.users} users + ${data.creators} creators).` });
      await refresh();
    } catch (e) {
      setMsg({ kind: 'error', text: 'Batch create failed — ' + (e.response?.data?.detail || e.message) });
    } finally {
      setBusy(false);
    }
  };

  const toggleSim = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const path = sim?.enabled ? '/admin/seed/simulator/stop' : '/admin/seed/simulator/start';
      await api.post(path);
      await refresh();
    } catch (e) {
      setMsg({ kind: 'error', text: 'Toggle failed — ' + (e.response?.data?.detail || e.message) });
    } finally {
      setBusy(false);
    }
  };

  const impersonate = async (u) => {
    if (!window.confirm(`Impersonate @${u.handle}? Your admin session will be paused. Use the red banner to exit.`)) return;
    try {
      const { data } = await api.post(`/admin/seed/users/${u.id}/impersonate`, { ttl_minutes: 120 });
      // Stash admin token, swap in impersonation JWT, then reload as that user
      const TOKEN_KEY = 'clanster_access_token';
      const adminToken = localStorage.getItem(TOKEN_KEY);
      if (adminToken) localStorage.setItem('admin_backup_token', adminToken);
      localStorage.setItem(TOKEN_KEY, data.access_token);
      localStorage.setItem('impersonating_user_id', u.id);
      localStorage.setItem('impersonating_handle', u.handle || '');
      window.location.href = '/home';
    } catch (e) {
      alert('Impersonation failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const fund = async (u) => {
    const raw = window.prompt(`Fund @${u.handle} how many pts?`, '100');
    if (!raw) return;
    const amount = parseFloat(raw);
    if (!isFinite(amount) || amount <= 0) return alert('Enter a positive number');
    try {
      const { data } = await api.post(`/admin/seed/users/${u.id}/fund`, { amount });
      setMsg({ kind: 'success', text: `Funded @${u.handle} with ${amount} pts. New balance: ${data.new_balance}.` });
      await refresh();
    } catch (e) {
      setMsg({ kind: 'error', text: 'Fund failed — ' + (e.response?.data?.detail || e.message) });
    }
  };

  return (
    <AdminLayout active="seed">
      <div className="admin-page" data-testid="admin-seed-page">
        <div className="admin-page-head">
          <div>
            <h1 className="admin-page-title">Seed Network</h1>
            <p className="admin-page-sub">
              Founder-controlled bootstrap accounts. Every account is <code>is_seed=true</code> and
              tracked separately from real deposits. Toggle the simulator on to auto-trade / post; toggle
              off before real user acquisition scales.
            </p>
          </div>
        </div>

        {msg && (
          <div className={`admin-banner ${msg.kind}`} data-testid="admin-seed-msg">
            {msg.text}
          </div>
        )}

        {/* Sandbox-tab recovery banner. If sessionStorage carries a sandbox
            JWT, every /admin/* call from this tab would 403. Give the user
            a single-click path back to their admin identity (localStorage
            still holds the original admin JWT). */}
        {inSandboxTab && (
          <div
            data-testid="admin-seed-sandbox-recovery"
            style={{
              marginBottom: 16,
              padding: 14,
              borderRadius: 12,
              border: '1px solid #F59E0B',
              background: 'rgba(245, 158, 11, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <div>
              <div style={{ fontWeight: 700, color: '#F59E0B', marginBottom: 4 }}>
                🧪 You&apos;re viewing admin from a sandbox tab
              </div>
              <div style={{ fontSize: 13, opacity: 0.8 }}>
                This tab is signed in as @{sessionStorage.getItem('sandbox_handle')} — admin actions will fail here. Click to restore your admin session in this tab.
              </div>
            </div>
            <button
              className="admin-btn primary"
              onClick={returnToAdminSession}
              data-testid="admin-seed-return-to-admin"
            >
              ↩ Return to admin session
            </button>
          </div>
        )}

        {/* Sandbox Launcher — each account opens in a NEW TAB with its own
            sessionStorage-scoped identity. Admin session in this tab stays
            intact so you can juggle 11 identities at once without incognito. */}
        <div className="seed-sim-card" data-testid="admin-test-accounts-card" style={{ marginBottom: 16 }}>
          <div className="seed-sim-head">
            <div>
              <div className="seed-sim-title">
                <span aria-hidden="true">🧪</span>
                Sandbox Launcher (1 creator + 10 fans)
              </div>
              <div className="seed-sim-sub">
                Each card opens in a NEW TAB with its own identity (per-tab sessionStorage). Your admin session in this tab stays active — no incognito needed. 8-hour token; close the tab to end the session.
              </div>
            </div>
            <button
              className="admin-btn primary"
              onClick={resetTestAccounts}
              disabled={testBusy}
              data-testid="admin-test-accounts-reset"
            >
              {testBusy ? 'Resetting…' : (testAccounts.creator ? '🔄 Reset all 11' : '✨ Create all 11')}
            </button>
          </div>
          {(testAccounts.creator || testAccounts.fan_1) && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginTop: 12 }}>
              {[
                { key: 'creator', acct: testAccounts.creator, label: 'Creator · 10,000 pts', tone: '#FFC107' },
                { key: 'fan_1',   acct: testAccounts.fan_1,   label: 'Fan 1 · 10,000 pts',   tone: '#00B4FF' },
                { key: 'fan_2',   acct: testAccounts.fan_2,   label: 'Fan 2 · 10,000 pts',   tone: '#B4FF00' },
                { key: 'fan_3',   acct: testAccounts.fan_3,   label: 'Fan 3 · 10,000 pts',   tone: '#FF66C4' },
                { key: 'fan_4',   acct: testAccounts.fan_4,   label: 'Fan 4 · 10,000 pts',   tone: '#8B5CF6' },
                { key: 'fan_5',   acct: testAccounts.fan_5,   label: 'Fan 5 · 10,000 pts',   tone: '#5AE07A' },
                { key: 'fan_6',   acct: testAccounts.fan_6,   label: 'Fan 6 · 10,000 pts',   tone: '#FF9F45' },
                { key: 'fan_7',   acct: testAccounts.fan_7,   label: 'Fan 7 · 10,000 pts',   tone: '#00E5FF' },
                { key: 'fan_8',   acct: testAccounts.fan_8,   label: 'Fan 8 · 10,000 pts',   tone: '#B4FF00' },
                { key: 'fan_9',   acct: testAccounts.fan_9,   label: 'Fan 9 · 10,000 pts',   tone: '#8B5CF6' },
                { key: 'fan_10',  acct: testAccounts.fan_10,  label: 'Fan 10 · 10,000 pts',  tone: '#FF6B6B' },
              ].map(({ key, acct, label, tone }) => acct && (
                <div key={key} style={{ padding: 12, borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: `1px solid ${tone}44` }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: tone, marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{acct.display_name}</div>
                  <div style={{ fontSize: 11, opacity: 0.65, marginBottom: 8 }}>@{acct.handle}</div>
                  <button
                    className="admin-btn"
                    onClick={() => openSandboxTab(acct)}
                    data-testid={`admin-test-impersonate-${key}`}
                    style={{ width: '100%', padding: '6px', fontSize: 12 }}
                    title="Open in a new tab with sessionStorage-scoped identity"
                  >
                    Open in new tab ↗
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Simulator card */}
        <div className="seed-sim-card" data-testid="seed-sim-card">
          <div className="seed-sim-head">
            <div>
              <div className="seed-sim-title">
                <span className={`seed-sim-dot ${sim?.enabled ? 'on' : 'off'}`} />
                Simulator {sim?.enabled ? 'RUNNING' : 'STOPPED'}
              </div>
              <div className="seed-sim-sub">
                {sim?.enabled
                  ? `Bots acting every ${sim?.min_gap_s}-${sim?.max_gap_s}s · ${sim?.actions_today || 0} actions today`
                  : 'No autonomous activity. Only manual impersonation trades will run.'}
              </div>
            </div>
            <button
              className={`admin-btn ${sim?.enabled ? '' : 'primary'} seed-sim-btn`}
              onClick={toggleSim}
              disabled={busy || users.length === 0}
              data-testid="seed-sim-toggle"
              title={users.length === 0 ? 'Create seed accounts first' : ''}
            >
              {sim?.enabled ? 'Stop simulator' : 'Start simulator'}
            </button>
          </div>
          {sim?.recent && sim.recent.length > 0 && (
            <div className="seed-sim-log">
              <div className="seed-sim-log-title">Recent activity</div>
              <div className="seed-sim-log-body">
                {sim.recent.map((r, idx) => (
                  <div key={idx} className="seed-sim-log-row">
                    <span className="seed-sim-log-time">{formatTime(r.at)}</span>
                    <span className={`seed-sim-log-tag t-${r.action?.toLowerCase() || 'x'}`}>{r.action}</span>
                    <span className="seed-sim-log-handle">@{r.handle}</span>
                    <span className="seed-sim-log-detail">{r.detail}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Pool + actions */}
        <div className="admin-metrics-row">
          <MetricCard label="Seed accounts" value={users.length.toString()} testid="seed-count" />
          <MetricCard label="Creator slots" value={users.filter(u => u.is_creator).length.toString()} testid="seed-creator-count" />
          <MetricCard label="Total funded" value={`${Math.round(totalFunded).toLocaleString()} pts`} testid="seed-total-funded" />
        </div>

        <div className="admin-search-row">
          <button
            type="button"
            className="admin-btn primary"
            onClick={createBatch}
            disabled={busy || users.length > 0}
            data-testid="seed-create-batch-btn"
            title={users.length > 0 ? 'Batch already created' : ''}
          >
            {users.length > 0 ? 'Initial batch already created' : 'Create initial batch (12 + 6)'}
          </button>
          {users.length > 0 && (
            <button
              type="button"
              className="admin-btn danger"
              onClick={resetSeedNetwork}
              disabled={busy}
              data-testid="seed-reset-network-btn"
              title="Delete every seed user + their clans, then start fresh"
              style={{ marginLeft: 8 }}
            >
              🔥 Delete & reseed all
            </button>
          )}
        </div>

        <div className="admin-table-wrap">
          <table className="admin-table" data-testid="admin-seed-table">
            <thead>
              <tr>
                <th>Handle</th>
                <th>Kind</th>
                <th className="num">Balance</th>
                <th>Last action</th>
                <th className="admin-tbl-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && users.length === 0 && (
                <tr><td colSpan={5} className="admin-table-empty">Loading…</td></tr>
              )}
              {!loading && users.length === 0 && (
                <tr><td colSpan={5} className="admin-table-empty">No seed accounts yet. Click "Create initial batch" above to bootstrap.</td></tr>
              )}
              {users.map((u) => (
                <tr key={u.id} data-testid={`seed-row-${u.handle}`}>
                  <td>
                    <div className="admin-user-name">{u.display_name}</div>
                    <div className="admin-user-handle">@{u.handle}</div>
                  </td>
                  <td>
                    <span className={`seed-kind-pill ${u.is_creator ? 'creator' : 'user'}`}>
                      {u.is_creator ? 'Creator' : 'User'}
                    </span>
                  </td>
                  <td className="num">{(u.points_balance || 0).toFixed(2)}</td>
                  <td className="admin-cell-muted">{u.last_seed_action_at ? formatTime(u.last_seed_action_at) : '—'}</td>
                  <td className="admin-tbl-actions">
                    <button className="admin-btn" onClick={() => impersonate(u)} data-testid={`seed-impersonate-${u.handle}`}>Impersonate</button>
                    <button className="admin-btn" onClick={() => fund(u)} data-testid={`seed-fund-${u.handle}`}>Fund</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}

function MetricCard({ label, value, testid }) {
  return (
    <div className="admin-metric-card" data-testid={testid}>
      <div className="admin-metric-label">{label}</div>
      <div className="admin-metric-value">{value}</div>
    </div>
  );
}

function formatTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const diff = Date.now() - d.getTime();
  if (diff < 60000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}
